<script setup lang="ts">
import type { SelectContentEmits, SelectContentProps } from 'reka-ui';
import type { HTMLAttributes } from 'vue';
import { reactiveOmit } from '@vueuse/core';
import {
  SelectContent,
  SelectPortal,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectViewport,
  useForwardPropsEmits,
} from 'reka-ui';
import { ChevronDown } from '@lucide/vue';
import { cn } from '@/utils/shadcn';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<SelectContentProps & { class?: HTMLAttributes['class']; bodyLock?: boolean }>(),
  {
    position: 'popper',
    sideOffset: 4,
    bodyLock: false,
  },
);
const emits = defineEmits<SelectContentEmits>();

const delegatedProps = reactiveOmit(props, 'class');
const forwarded = useForwardPropsEmits(delegatedProps, emits);
</script>

<template>
  <SelectPortal>
    <SelectContent
      data-slot="select-content"
      v-bind="{ ...$attrs, ...forwarded }"
      :class="cn(
        'z-[2100] max-h-[260px] min-w-[var(--reka-select-trigger-width)] overflow-hidden bg-surface-card border border-border-subtle rounded-lg shadow-[var(--shadow-lg)]',
        props.class,
      )"
    >
      <SelectScrollUpButton class="flex items-center justify-center h-7 text-text-tertiary">
        <ChevronDown class="size-4 rotate-180" />
      </SelectScrollUpButton>
      <SelectViewport class="p-1.5">
        <slot></slot>
      </SelectViewport>
      <SelectScrollDownButton class="flex items-center justify-center h-7 text-text-tertiary">
        <ChevronDown class="size-4" />
      </SelectScrollDownButton>
    </SelectContent>
  </SelectPortal>
</template>
